/**
 * MY REPORT CARD for a cohort (E2) — the record's "Grades" tab.
 *
 * Does NOT reuse `assessment/grades-tab.tsx`: that's the TEACHER's gradebook
 * and asks `GET /api/cohorts/:id/gradebook`, the matrix of the ENTIRE
 * roster. A student shouldn't see it —and the endpoint wouldn't give it to
 * them anyway— but they should see their own row with the weights, and that
 * is already on their device: `grade_category` and `grade_item` sync to the
 * whole cohort (weight transparency) and `assignment_grade` only brings
 * their own rows.
 *
 * Weighting is done by `buildReportCard` (@space/shared), a pure mirror of
 * Rust's `weighted_final`. Besides the official grade it shows the
 * PROJECTED one — weighting only what's already graded — because mid-course
 * the official one counts everything pending as zero and tells you nothing.
 */
import { type Component, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { useCohort } from "../../hooks/use-cohort-data";
import { useActiveGradingScale } from "../../hooks/use-grading-scale";
import { useMyEnrollment, useMyReportCard } from "../../hooks/use-student-record";

export const CohortReportCard: Component<{ cohortId: string; cohortName: string }> = (props) => {
  const { t } = useI18n();
  const cohortId = () => props.cohortId;
  const cohort = useCohort(cohortId);
  const courseId = () => cohort.data?.course_id ?? undefined;
  const orgId = () => cohort.data?.organization_id ?? undefined;
  const enrollment = useMyEnrollment(cohortId);
  const card = useMyReportCard(cohortId, courseId);

  // SINGLE resolution point (cohort → course → org → default precedence,
  // same as the server's). This used to look only at the legacy key, so a
  // scale DECLARED by the academy (`grading_model_id`) was honored by the
  // engine and ignored right here, on the screen the student reads.
  const scheme = useActiveGradingScale({ organizationId: orgId, courseId, cohortId });

  const sealed = () => enrollment.row()?.finalGrade ?? null;
  const hasItems = () => card().cells.length > 0;

  return (
    <section class="space-y-3 rounded-2xl border border-border p-4" data-testid="report-card">
      <h3 class="font-display text-base font-bold">{props.cohortName}</h3>

      <Show
        when={hasItems()}
        fallback={
          <p class="text-sm text-muted-foreground" data-testid="report-card-empty">
            {t(msg`Your teacher hasn't set up the gradebook for this cohort yet.`)}
          </p>
        }
      >
        {/* Summary: projected + official (or the final sealed at closing). */}
        <div class="grid gap-2 sm:grid-cols-2">
          <div class="rounded-xl bg-accent/10 p-3" data-testid="report-card-projected">
            <p class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t(msg`Projected grade`)}
            </p>
            <p class="font-display text-2xl font-extrabold text-accent">
              {scheme().toDisplay(card().projectedRaw)}
            </p>
            <p class="text-xs text-muted-foreground">
              {t(msg`Weighing only what has been graded`)} · {card().gradedCount}/
              {card().gradableCount}
            </p>
          </div>
          <div class="rounded-xl bg-muted/40 p-3" data-testid="report-card-official">
            <p class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Show when={sealed() !== null} fallback={t(msg`Grade so far`)}>
                {t(msg`Final grade`)}
              </Show>
            </p>
            <p class="font-display text-2xl font-extrabold">
              <Show
                when={sealed() !== null}
                fallback={<>{scheme().displayValue(scheme().convert(card().officialRaw))}</>}
              >
                {scheme().displayValue(sealed()!)}
              </Show>
            </p>
            <p class="text-xs text-muted-foreground">
              <Show
                when={sealed() !== null}
                fallback={t(msg`Anything not graded yet counts as zero`)}
              >
                {t(msg`Sealed when the cohort closed`)}
              </Show>
            </p>
          </div>
        </div>

        {/* Breakdown by category, with the weight visible. */}
        <For each={card().categories}>
          {(group) => (
            <div class="space-y-1" data-testid="report-card-category">
              <p class="flex items-baseline justify-between text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <span>{group.name || t(msg`Ungrouped`)}</span>
                <span data-testid="report-card-weight">
                  {t(msg`Weight`)} {group.weight}
                  <Show when={group.dropLowest > 0}>
                    {" · "}
                    {t(msg`drops lowest`)} {group.dropLowest}
                  </Show>
                </span>
              </p>
              <ul class="space-y-1">
                <For each={group.cells}>
                  {(cell) => (
                    <li
                      class="flex items-center justify-between rounded-lg border border-border px-3 py-1.5 text-sm"
                      data-testid="report-card-item"
                      data-graded={cell.graded ? "true" : "false"}
                    >
                      <span class="min-w-0 truncate">{cell.name}</span>
                      <span class="ms-2 shrink-0 font-semibold">
                        <Show
                          when={cell.excused}
                          fallback={
                            <Show
                              when={cell.graded}
                              fallback={
                                <span class="text-xs font-normal text-muted-foreground">
                                  {t(msg`Pending`)}
                                </span>
                              }
                            >
                              {Math.round((cell.score ?? 0) * 10) / 10} / {cell.maxScore}
                            </Show>
                          }
                        >
                          <span class="text-xs font-normal text-muted-foreground">
                            {t(msg`Excused`)}
                          </span>
                        </Show>
                      </span>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          )}
        </For>

        {/* The footer used to repeat both grades in RAW PERCENTAGE, below a
            headline that WAS in the active scale: the same grade with two
            different numbers. It's in the active scale now, whatever it is. */}
        <p class="text-xs text-muted-foreground" data-testid="report-card-raw">
          {t(msg`Projected`)} {scheme().toDisplay(card().projectedRaw)} · {t(msg`So far`)}{" "}
          {scheme().toDisplay(card().officialRaw)}
        </p>
      </Show>
    </section>
  );
};
